import { FunctionComponent, useContext, useEffect, useState } from 'react'
import { Box, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import ReportActionService from '../../../shared/services/report-action/report-action.service'
import UserDetailsService from '../../../shared/services/user-details/user-details.service'
import { IReportAction } from '../../../shared/services/report-action/report-action.model'
import { IUserDetails } from '../../../shared/services/user-details/user-details.model'
import { actionEntityName, friendlyActionTitle } from '../../../shared/services/report-action/report-action-title'
import { UserDetailsContext } from '../../../context/user-details/user-details'
import { IHeadCell } from '../../../shared/interfaces/table-display.model'
import { TableDisplay } from '../miscellaneous-components/table-display'
import { InformationBox } from '../miscellaneous-components/information-box'
import { IconName } from '../../../shared/interfaces/icon-picker.model'
import { AssignActionOwner } from '../emissons-components/overview/emissions-actions/assign-action-owner'
import { ActionStatusChip } from '../emissons-components/overview/emissions-actions/action-status-chip'
import { ReassignAllActionsDialog } from './reassign-all-actions-dialog'

// The organisation-wide task view of the readiness page: every report action for the period,
// with the same status vocabulary, owner assignment (individual and bulk) and row-to-entry-form
// navigation as /my-tasks and the group action cards.
export const ReadinessTasksPanel: FunctionComponent<{
  reportPeriodId: string,
  locked: boolean,
}> = ({ reportPeriodId, locked }) => {

  const navigate = useNavigate()
  const reportActionService = new ReportActionService()
  const userDetailsContext = useContext(UserDetailsContext)
  const userDetails = userDetailsContext.userDetails
  const isAdmin = !!userDetails?.roles.includes('admin')

  const [status, setStatus] = useState<'outstanding' | 'upcoming' | 'completed'>('outstanding')
  const [actions, setActions] = useState<IReportAction[]>([])
  const [users, setUsers] = useState<IUserDetails[]>([])
  const [hasFetched, setHasFetched] = useState(false)
  const [totalRecords, setTotalRecords] = useState(0)
  const [inputPage, setInputPage] = useState(0)
  const [perPage, setPerPage] = useState(8)
  const [selected, setSelected] = useState<string[]>([])

  const loadActions = () => {
    reportActionService.getReportActionsWithPagination(reportPeriodId, `&status=${status}`, inputPage, perPage)
      .then(response => { setActions(response.rows); setTotalRecords(response.count); setHasFetched(true) })
      .catch(err => console.log(err))
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(loadActions, [reportPeriodId, status, inputPage, perPage])

  useEffect(() => {
    if (!isAdmin) return
    new UserDetailsService().getAllUserDetails(`?status=active`)
      .then((response) => {
        setUsers(response.filter(user => !(user.inviteSent === true && user.inviteAccepted === false)))
      })
      .catch(err => console.log(err))
  }, [isAdmin])

  const statusHandler = (_: React.MouseEvent<HTMLElement>, next: typeof status | null) => {
    if (next) { setStatus(next); setInputPage(0) }
  }

  // Row click opens the same action-entry flow used everywhere else. Tasks created from
  // readiness findings have no entity entry form; they carry the finding's deep link instead.
  const handleSelected = (rowIds: string[]) => {
    if (!rowIds[0]) return
    const action = actions.find(a => a.id === rowIds[0])
    if (action?.actionCode === ('readiness' as IReportAction['actionCode'])) {
      navigate(action.deepLink || '/readiness')
      return
    }
    reportActionService.checkIfActionReportOrReduction(rowIds[0])
      .then(response => {
        if (response.type === 'reportAction') {
          navigate(`/emissions/overview/group/${response.action.groupId}?actionId=${response.action.id}`, { state: { response } })
        }
      })
      .catch(err => console.log(err))
  }

  const headCells: IHeadCell[] = [
    { id: 'title', numeric: false, label: 'Task', sortable: false },
    { id: 'entity', numeric: false, label: 'Entity', sortable: false },
    { id: 'owner', numeric: false, label: 'Owner', sortable: false },
    { id: 'date', numeric: false, label: 'Delivery date', sortable: false },
  ]
  const rows = actions.map(action => ({
    id: action.id,
    title: action.status
      ? <Stack direction='row' alignItems='center' gap={1}>
        <Typography variant='body2'>{friendlyActionTitle(action)}</Typography>
        <ActionStatusChip status={action.status} reason={action.statusReason} />
      </Stack>
      : friendlyActionTitle(action),
    entity: actionEntityName(action),
    owner: <AssignActionOwner
      reportAction={action}
      users={users}
      disabled={!isAdmin || locked || action.completed}
      onAssigned={loadActions}
    />,
    date: action.actionBillingPeriod === 'annually'
      ? (moment(action.startDate).year() === moment(action.endDate).year()
        ? moment(action.startDate).year()
        : `${moment(action.startDate).year()} - ${moment(action.endDate).year()}`)
      : moment(action.endDate).format('D MMM YYYY'),
  }))

  return (
    <Box>
      <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: 2 }} useFlexGap flexWrap='wrap' gap={1}>
        <ToggleButtonGroup value={status} exclusive onChange={statusHandler}>
          <ToggleButton value='outstanding' sx={{ py: 0.75, width: '160px', textTransform: 'none' }}>Outstanding</ToggleButton>
          <ToggleButton value='upcoming' sx={{ py: 0.75, width: '160px', textTransform: 'none' }}>Upcoming</ToggleButton>
          <ToggleButton value='completed' sx={{ py: 0.75, width: '160px', textTransform: 'none' }}>Completed</ToggleButton>
        </ToggleButtonGroup>
        {isAdmin && !locked &&
          <ReassignAllActionsDialog reportPeriodId={reportPeriodId} users={users} onReassigned={loadActions} />}
      </Stack>

      {hasFetched && actions.length === 0
        ? <InformationBox icon={IconName.CheckCircle} text={`There are no ${status} tasks for this reporting period.`} marginTop={1} />
        : <TableDisplay
          headCells={headCells}
          rows={rows}
          selected={selected}
          setSelected={setSelected}
          handleSelected={handleSelected}
          inputPage={inputPage}
          setInputPage={setInputPage}
          totalRecords={totalRecords}
          perPage={perPage}
          setPerPage={setPerPage}
          loading={!hasFetched} />}
    </Box>
  )
}
